/*
 * Cart drawer — drawer-specific logic only. Opening, closing, focus trapping,
 * Escape, click-outside, scroll lock and anchoring all come from the shared
 * OverlayPanel (assets/overlay-panel.js), the same one the header uses; this
 * panel just anchors to the right instead of the left. Quantity changes, size
 * switches, removes, the re-render and the header count come from the shared
 * cart client (assets/cart-lines.js), the same one the cart page uses.
 *
 * Progressive enhancement: without this script (or without the section added to
 * the Overlay group) the header's cart icon stays a plain link to /cart.
 */

// Bare names, resolved by the import map in layout/theme.liquid to the
// versioned asset URLs (a relative './' path would bypass cache-busting).
import { OverlayPanel } from 'overlay-panel';
import { createCart } from 'cart-lines';

const init = (drawer) => {
  if (!drawer || drawer.dataset.cartReady) return;
  // The editor's picker thumbnail (visual preview mode) is a flat, open render of
  // the drawer (.overlay--preview): a picture of it, with nothing to wire.
  if (window.Shopify && window.Shopify.visualPreviewMode) return;
  drawer.dataset.cartReady = '1';

  const dialog = drawer.querySelector('[data-cart-dialog]');
  const header = document.querySelector('[data-store-header]');
  // The header's icons group: on desktop the drawer opens level with it, over
  // the header, its right edge in line with the group's.
  const anchor = header ? header.querySelector('[data-header-actions]') || header.querySelector('[data-header-bar]') : null;
  const cartLinks = [...document.querySelectorAll('[data-cart-link]')];

  const panel = new OverlayPanel({
    root: drawer,
    dialog,
    toggle: cartLinks[0] || null,
    anchor,
    origin: 'right',
  });

  const cart = createCart({ root: drawer, sectionId: drawer.dataset.sectionId });

  drawer.addEventListener('click', (event) => {
    if (event.target.closest('[data-cart-close]')) panel.close();
  });

  // Take over the header's cart link while the drawer exists.
  cartLinks.forEach((link) => {
    link.addEventListener('click', (event) => {
      event.preventDefault();
      panel.open();
    });
  });

  // Let other code open or refresh the drawer.
  document.addEventListener('cart:open', () => panel.open());
  document.addEventListener('cart:refresh', () => cart.refresh());

  /* Add to cart on another component's behalf — the product card's quick add,
     the product page's form — through the shared client, so the add, the
     re-render, the header count and any error announcement share one code
     path and one round trip. `handled` is set synchronously so the caller
     knows a drawer took it and doesn't fall back; `done(ok, message)` reports
     the outcome, with the server's wording when there is something to say.

     A refused add can still have changed the cart: asking for more than is in
     stock gets a 422 ("Only 1 item was added to your cart due to
     availability") with the possible part added. So on a refusal the cart is
     re-read, and if it grew the drawer opens on it as for a success.

     Whatever Shopify had to say is shown in the cart, to everyone
     (cart.notify: text with role="alert"), not only spoken. A caller with
     nowhere of its own to show a refusal - a product card - sets
     `showInCart`, and the drawer opens on a refusal too, with the reason at
     its top; the product page shows it under its own button instead. */
  document.addEventListener('cart:add', async (event) => {
    const detail = event.detail || {};
    if (!detail.variantId) return;
    detail.handled = true;
    const before = cart.currentCount();
    const item = { id: Number(detail.variantId), quantity: detail.quantity || 1 };
    // Line item properties: a gift card's recipient, an app's personalisation.
    if (detail.properties && Object.keys(detail.properties).length) item.properties = detail.properties;
    // Files go as properties too (a print's crop reference image): Shopify
    // keeps the file and the property becomes its link. That takes a form
    // post, not JSON.
    let body = { items: [item] };
    const files = detail.files ? Object.entries(detail.files).filter(([, file]) => file instanceof Blob) : [];
    if (files.length) {
      body = new FormData();
      body.set('id', String(item.id));
      body.set('quantity', String(item.quantity));
      Object.entries(item.properties || {}).forEach(([name, value]) => body.set(`properties[${name}]`, value));
      files.forEach(([name, file]) => body.set(`properties[${name}]`, file, file.name || `${name}.jpg`));
    }
    const data = await cart.request('/cart/add.js', body);
    let ok = Boolean(data);
    let message = null;
    if (!ok) {
      message = cart.lastError;
      const fresh = await cart.refresh();
      if (fresh && fresh.item_count > before) ok = true;
    }
    const show = Boolean(message) && (ok || detail.showInCart);
    if (ok || show) panel.open();
    if (show) cart.notify(message);
    if (typeof detail.done === 'function') detail.done(ok, message, show, ok ? null : cart.lastErrorFields);
  });

  drawer.cartDrawer = panel;
};

const initAll = () => document.querySelectorAll('[data-cart-drawer]').forEach(init);

if (document.readyState !== 'loading') initAll();
else document.addEventListener('DOMContentLoaded', initAll);

// Theme editor: re-wire a freshly reloaded drawer section.
document.addEventListener('shopify:section:load', (event) => {
  const drawer = event.target.querySelector('[data-cart-drawer]');
  if (drawer) init(drawer);
});
